"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight, ChevronDown, ChevronUp, GitCompare, Plus, X } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import { addCompareProduct, clearCompareProducts, removeCompareProduct, replaceCompareProducts, selectCompareProducts } from "@/Redux/slice/compareSlice";
import { buildCompareProductsParam, getCompareProductKey, isSameCompareProduct, getComparePath, isComparePath, MAX_COMPARE_PRODUCTS, normalizeCompareProduct } from "@/utils/compareProducts";
import AddByIndication from "@/Components/DoctorsDashboard/Compare/AddByIndication";
const STORAGE_PREFIX = "alpha-md:quick-compare";
const COMPARE_ADD_ANIMATION_EVENT = "alpha-md:compare-add-animation";
const COMPARE_REMOVE_ANIMATION_EVENT = "alpha-md:compare-remove-animation";
const FLIGHT_DURATION_MS = 1000;
const typeLabel = type => {
  const normalizedType = String(type || "").toLowerCase();
  if (normalizedType === "otc") return "OTC";
  if (normalizedType === "vaccine") return "Vaccine";
  if (normalizedType === "biologic") return "Biologic";
  return "Rx";
};
const CompareTray = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const products = useSelector(selectCompareProducts);
  const user = useSelector(state => state?.userData?.userData?.user);
  const [hydratedScope, setHydratedScope] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [visualCount, setVisualCount] = useState(0);
  const [countPulseToken, setCountPulseToken] = useState(0);
  const previousScopeRef = useRef("");
  const trayRef = useRef(null);
  const launcherRef = useRef(null);
  const activeFlightsRef = useRef(new Set());
  const animationFramesRef = useRef(new Set());
  const pendingFlightsRef = useRef(0);
  const latestProductsLengthRef = useRef(products.length);
  const launcherImpactRef = useRef(null);
  const cancelFlightByProductRef = useRef(() => {});
  const cancelAllFlightsRef = useRef(() => {});
  const userType = user?.type;
  const userId = user?._id || user?.id;
  // Compare is read-only (FDA label facts + review aggregates), so reps get
  // the same tray as doctors. `storageScope` is keyed by type + id, so each
  // account keeps its own selections.
  const isEligibleUser = userType === "physician" || userType === "clinic" || userType === "reps";
  const storageScope = isEligibleUser && userId ? `${userType}:${String(userId)}` : "";
  const storageKey = storageScope ? `${STORAGE_PREFIX}:${encodeURIComponent(storageScope)}` : "";

  // Restore only the current account's choices. Clearing Redux when the
  // account changes prevents one user from briefly seeing another user's tray.
  useEffect(() => {
    if (!storageScope) {
      if (previousScopeRef.current) dispatch(clearCompareProducts());
      previousScopeRef.current = "";
      setHydratedScope("");
      setIsExpanded(false);
      return;
    }
    if (previousScopeRef.current === storageScope) return;
    previousScopeRef.current = storageScope;
    setIsExpanded(false);
    let savedProducts = [];
    try {
      const saved = window.sessionStorage.getItem(storageKey);
      savedProducts = saved ? JSON.parse(saved) : [];
      if (!Array.isArray(savedProducts)) savedProducts = [];
    } catch {
      savedProducts = [];
    }
    dispatch(replaceCompareProducts(savedProducts));
    setHydratedScope(storageScope);
  }, [dispatch, storageKey, storageScope]);

  // Do not write the initial empty Redux state over a stored selection before
  // the account-specific restore above has completed.
  useEffect(() => {
    if (!storageKey || hydratedScope !== storageScope) return;
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(products));
    } catch {
      // Comparison still works for this page when browser storage is blocked.
    }
  }, [hydratedScope, products, storageKey, storageScope]);

  // Open the panel as soon as a medicine is added from anywhere in the app,
  // so the selection and the suggestions are visible without hunting for the
  // pill. Restoring a saved selection on page load must not trigger this,
  // hence the hydration guard and the baseline reset below.
  const previousCountRef = useRef(products.length);
  const openedForScopeRef = useRef("");
  // Set when an add happened while a token was mid-flight.
  const awaitingFlightRef = useRef(false);
  useEffect(() => {
    const isHydrated = Boolean(storageScope) && hydratedScope === storageScope;
    if (!isHydrated) {
      previousCountRef.current = products.length;
      return;
    }

    // First pass after this account's selection is restored: adopt the
    // restored count as the baseline instead of treating it as an add.
    if (openedForScopeRef.current !== storageScope) {
      openedForScopeRef.current = storageScope;
      previousCountRef.current = products.length;
      return;
    }
    const grew = products.length > previousCountRef.current;
    previousCountRef.current = products.length;
    if (!grew) return;

    // Opening immediately cut across the medicine token still flying to the
    // launcher. Wait for that animation to land, then open — so the panel
    // appears as the arrival's result rather than on top of it.
    if (pendingFlightsRef.current > 0) {
      awaitingFlightRef.current = true;
      return;
    }
    setIsExpanded(true);
  }, [products.length, hydratedScope, storageScope]);

  // A flight finishing bumps `countPulseToken`. When the last one lands and an
  // add is waiting on it, open the panel. The timeout is a safety net for the
  // reduced-motion path, where no flight is ever queued.
  useEffect(() => {
    if (!awaitingFlightRef.current) return;
    if (pendingFlightsRef.current > 0) return;
    awaitingFlightRef.current = false;
    const id = window.setTimeout(() => setIsExpanded(true), 90);
    return () => window.clearTimeout(id);
  }, [countPulseToken]);
  useEffect(() => {
    latestProductsLengthRef.current = products.length;
    if (pendingFlightsRef.current === 0) {
      setVisualCount(products.length);
      return;
    }

    // Removals stay visually immediate even if an earlier add animation is
    // still finishing.
    setVisualCount(count => Math.min(count, products.length));
  }, [products.length]);

  // Compare buttons publish their viewport origin before updating Redux. The
  // next animation frame lets the launcher mount for a first selection, then
  // an aria-hidden medicine token flies to its real on-screen position.
  useEffect(() => {
    const playLauncherImpact = () => {
      const launcher = launcherRef.current;
      if (!launcher || typeof launcher.animate !== "function") return;
      launcherImpactRef.current?.cancel();
      const impactAnimation = launcher.animate([{
        boxShadow: "0 10px 28px rgba(53, 78, 240, 0.34)",
        transform: "translateY(0) scale(1)",
        offset: 0
      }, {
        boxShadow: "0 0 0 7px rgba(82, 104, 255, 0.18), 0 18px 40px rgba(53, 78, 240, 0.52)",
        transform: "translateY(-2px) scale(1.085)",
        offset: 0.38
      }, {
        boxShadow: "0 12px 30px rgba(53, 78, 240, 0.4)",
        transform: "translateY(0) scale(0.975)",
        offset: 0.72
      }, {
        boxShadow: "0 10px 28px rgba(53, 78, 240, 0.34)",
        transform: "translateY(0) scale(1)",
        offset: 1
      }], {
        duration: 390,
        easing: "cubic-bezier(0.2, 0.8, 0.2, 1)"
      });
      launcherImpactRef.current = impactAnimation;
      impactAnimation.addEventListener("finish", () => {
        if (launcherImpactRef.current === impactAnimation) {
          launcherImpactRef.current = null;
        }
      }, {
        once: true
      });
    };
    const removeFlight = (record, pulseCount = false) => {
      if (!record || record.finished) return;
      record.finished = true;
      if (record.frameId) {
        window.cancelAnimationFrame(record.frameId);
        animationFramesRef.current.delete(record.frameId);
        record.frameId = null;
      }
      if (record.timeoutId) window.clearTimeout(record.timeoutId);
      if (record.animation?.playState === "running") {
        record.animation.cancel();
      }
      record.element?.remove();
      activeFlightsRef.current.delete(record);
      pendingFlightsRef.current = Math.max(0, pendingFlightsRef.current - 1);
      if (pulseCount) {
        playLauncherImpact();
        setVisualCount(count_0 => {
          if (pendingFlightsRef.current === 0) {
            return latestProductsLengthRef.current;
          }
          return Math.min(latestProductsLengthRef.current, Math.max(count_0, record.targetCount));
        });
        setCountPulseToken(token => token + 1);
      } else if (pendingFlightsRef.current === 0) {
        setVisualCount(latestProductsLengthRef.current);
      }
    };
    const handleCompareAdd = event => {
      const originX = Number(event?.detail?.origin?.x);
      const originY = Number(event?.detail?.origin?.y);
      if (!Number.isFinite(originX) || !Number.isFinite(originY)) return;
      if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
      }
      const requestedCount = Number(event?.detail?.nextCount);
      const animationProductKey = String(event?.detail?.productKey || "");
      if (animationProductKey && Array.from(activeFlightsRef.current).some(record_0 => record_0.productKey === animationProductKey)) {
        return;
      }
      const targetCount = Number.isFinite(requestedCount) ? Math.max(1, Math.min(MAX_COMPARE_PRODUCTS, requestedCount)) : Math.min(MAX_COMPARE_PRODUCTS, latestProductsLengthRef.current + 1);
      const record_1 = {
        animation: null,
        element: null,
        finished: false,
        frameId: null,
        productKey: animationProductKey,
        targetCount,
        timeoutId: null
      };
      pendingFlightsRef.current += 1;
      activeFlightsRef.current.add(record_1);
      const frameId = window.requestAnimationFrame(() => {
        animationFramesRef.current.delete(frameId);
        record_1.frameId = null;
        if (record_1.finished) return;

        // The wrapper keeps the launcher's final layout box even while the
        // button itself is playing its first-entry scale animation.
        const trayBounds = trayRef.current?.getBoundingClientRect();
        const launcherBounds = launcherRef.current?.getBoundingClientRect();
        const targetBounds = trayBounds?.width ? trayBounds : launcherBounds;
        const desktop = window.innerWidth >= 640;
        const largeDesktop = window.innerWidth >= 1024;
        const fallbackRight = largeDesktop ? 80 : desktop ? 56 : 20;
        const fallbackBottom = largeDesktop ? 80 : desktop ? 56 : 80;
        const targetX = targetBounds?.width ? targetBounds.left + targetBounds.width / 2 : window.innerWidth - fallbackRight - 72;
        const targetY = targetBounds?.height ? targetBounds.top + targetBounds.height / 2 : window.innerHeight - fallbackBottom - 24;
        const travelX = targetX - originX;
        const travelY = targetY - originY;
        const travelDistance = Math.hypot(travelX, travelY);
        const arcLift = Math.min(160, Math.max(80, travelDistance * 0.17));
        const controlX = originX + travelX * 0.54;
        const controlY = Math.max(28, Math.min(originY, targetY) - arcLift);
        const direction = travelX >= 0 ? 1 : -1;
        const pointOnQuadraticCurve = progress => {
          const inverse = 1 - progress;
          return {
            x: inverse * inverse * originX + 2 * inverse * progress * controlX + progress * progress * targetX,
            y: inverse * inverse * originY + 2 * inverse * progress * controlY + progress * progress * targetY
          };
        };
        const transformAt = (x, y, scale, rotation = 0) => `translate3d(${Math.round(x)}px, ${Math.round(y)}px, 0) translate(-50%, -50%) rotate(${rotation.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
        const flyer = document.createElement("div");
        flyer.setAttribute("aria-hidden", "true");
        Object.assign(flyer.style, {
          alignItems: "center",
          backdropFilter: "blur(10px)",
          background: "rgba(255, 255, 255, 0.97)",
          border: "1px solid #C8D1FF",
          borderRadius: "14px",
          boxShadow: "0 12px 30px rgba(31, 31, 31, 0.18)",
          color: "#1F1F1F",
          display: "flex",
          fontFamily: "Poppins, sans-serif",
          gap: "9px",
          left: "0",
          maxWidth: "220px",
          minWidth: "160px",
          opacity: "0",
          padding: "8px 10px",
          pointerEvents: "none",
          position: "fixed",
          top: "0",
          transform: transformAt(originX, originY, 0.72, -3 * direction),
          width: "min(210px, calc(100vw - 32px))",
          zIndex: "1000000"
        });
        const medicineName = String(event?.detail?.label || "Medicine").trim().slice(0, 60);
        const initial = medicineName.match(/[a-z0-9]/i)?.[0]?.toUpperCase() || "Rx";
        const badge = document.createElement("span");
        badge.textContent = initial;
        Object.assign(badge.style, {
          alignItems: "center",
          background: "linear-gradient(135deg, #354EF0 0%, #6175FF 100%)",
          borderRadius: "10px",
          color: "#FFFFFF",
          display: "inline-flex",
          flex: "0 0 auto",
          fontSize: initial === "Rx" ? "10px" : "13px",
          fontWeight: "700",
          height: "34px",
          justifyContent: "center",
          boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.22)",
          width: "34px"
        });
        const copy = document.createElement("span");
        Object.assign(copy.style, {
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          minWidth: "0"
        });
        const eyebrow = document.createElement("span");
        eyebrow.textContent = "Added to compare";
        Object.assign(eyebrow.style, {
          color: "#6E7DE2",
          fontSize: "9px",
          fontWeight: "600",
          letterSpacing: "0.02em",
          lineHeight: "1.25",
          textTransform: "uppercase"
        });
        const label = document.createElement("span");
        label.textContent = medicineName;
        Object.assign(label.style, {
          color: "#303030",
          display: "block",
          fontSize: "12px",
          fontWeight: "600",
          lineHeight: "1.35",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        });
        copy.append(eyebrow, label);
        flyer.append(badge, copy);
        document.body.appendChild(flyer);
        record_1.element = flyer;

        // Sample a quadratic Bezier curve into several Web Animation
        // keyframes. This produces a true parabolic lift instead of a single
        // straight interpolation through one midpoint.
        const flightKeyframes = [0, 0.05, 0.1, 0.18, 0.3, 0.44, 0.58, 0.7, 0.8, 0.88, 0.94, 1].map(progress_0 => {
          const point = pointOnQuadraticCurve(progress_0);
          const liftProgress = Math.min(1, progress_0 / 0.1);
          const landingProgress = Math.max(0, (progress_0 - 0.1) / 0.9);
          const scale_0 = progress_0 <= 0.1 ? 0.72 + 0.28 * liftProgress : 1 - 0.66 * Math.pow(landingProgress, 1.22);
          const rotation_0 = direction * (Math.sin(Math.PI * progress_0) * 5.5 - (1 - progress_0) * 3);
          const opacity = progress_0 < 0.05 ? progress_0 / 0.05 : progress_0 > 0.86 ? Math.max(0, (1 - progress_0) / 0.14) : 1;
          const shadowStrength = Math.sin(Math.PI * progress_0);
          return {
            boxShadow: `0 ${Math.round(12 + shadowStrength * 12)}px ${Math.round(30 + shadowStrength * 18)}px rgba(31, 31, 31, ${(0.18 + shadowStrength * 0.12).toFixed(3)})`,
            opacity,
            offset: progress_0,
            transform: transformAt(point.x, point.y, scale_0, rotation_0)
          };
        });
        try {
          record_1.animation = flyer.animate(flightKeyframes, {
            duration: FLIGHT_DURATION_MS,
            easing: "linear",
            fill: "forwards"
          });
          record_1.animation.addEventListener("finish", () => removeFlight(record_1, true), {
            once: true
          });
          record_1.animation.addEventListener("cancel", () => removeFlight(record_1, false), {
            once: true
          });
          record_1.timeoutId = window.setTimeout(() => removeFlight(record_1, true), FLIGHT_DURATION_MS + 160);
        } catch {
          removeFlight(record_1, true);
        }
      });
      record_1.frameId = frameId;
      animationFramesRef.current.add(frameId);
    };
    const cancelFlightByProduct = productKey => {
      if (!productKey) return;
      Array.from(activeFlightsRef.current).forEach(record_2 => {
        if (record_2.productKey === productKey) removeFlight(record_2, false);
      });
    };
    const cancelAllFlights = () => {
      Array.from(activeFlightsRef.current).forEach(record_3 => removeFlight(record_3, false));
    };
    const handleCompareRemove = event_0 => {
      cancelFlightByProduct(String(event_0?.detail?.productKey || ""));
    };
    cancelFlightByProductRef.current = cancelFlightByProduct;
    cancelAllFlightsRef.current = cancelAllFlights;
    window.addEventListener(COMPARE_ADD_ANIMATION_EVENT, handleCompareAdd);
    window.addEventListener(COMPARE_REMOVE_ANIMATION_EVENT, handleCompareRemove);
    return () => {
      window.removeEventListener(COMPARE_ADD_ANIMATION_EVENT, handleCompareAdd);
      window.removeEventListener(COMPARE_REMOVE_ANIMATION_EVENT, handleCompareRemove);
      cancelFlightByProductRef.current = () => {};
      cancelAllFlightsRef.current = () => {};
      animationFramesRef.current.forEach(frameId_0 => window.cancelAnimationFrame(frameId_0));
      animationFramesRef.current.clear();
      pendingFlightsRef.current = 0;
      launcherImpactRef.current?.cancel();
      launcherImpactRef.current = null;
      activeFlightsRef.current.forEach(record_4 => {
        record_4.finished = true;
        if (record_4.timeoutId) window.clearTimeout(record_4.timeoutId);
        record_4.animation?.cancel();
        record_4.element?.remove();
      });
      activeFlightsRef.current.clear();
    };
  }, []);

  // Close the panel if its final item is removed from any compare toggle.
  // This keeps the next selection's entry state compact instead of reopening
  // a panel that belonged to an earlier selection.
  useEffect(() => {
    if (products.length === 0 && isExpanded) setIsExpanded(false);
  }, [isExpanded, products.length]);
  useEffect(() => {
    if (!isExpanded) return undefined;
    const closeOnEscape = event_1 => {
      if (event_1.key !== "Escape") return;
      setIsExpanded(false);
      launcherRef.current?.focus();
    };
    const closeOnOutsidePress = event_2 => {
      if (!trayRef.current?.contains(event_2.target)) setIsExpanded(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("pointerdown", closeOnOutsidePress);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("pointerdown", closeOnOutsidePress);
    };
  }, [isExpanded]);
  if (!isEligibleUser || !storageScope || hydratedScope !== storageScope || products.length === 0 || isComparePath(pathname)) {
    return null;
  }
  const canCompare = products.length >= 2;
  const launcherCount = pendingFlightsRef.current > 0 ? visualCount : products.length;
  const isFirstSelectionLanding = pendingFlightsRef.current > 0 && launcherCount === 0;
  const selectionMessage = `${products.length} of ${MAX_COMPARE_PRODUCTS} selected`;
  const openComparison = () => {
    if (!canCompare) return;
    const params = new URLSearchParams({
      products: buildCompareProductsParam(products)
    });
    setIsExpanded(false);
    router.push(`${getComparePath(userType)}?${params.toString()}`);
  };

  // Quick-add from the tray: this is where a user sits right after picking
  // their first medicine, so the 2nd can be added without a trip to search.
  const addSuggested = rawProduct => {
    const normalized = normalizeCompareProduct(rawProduct);
    if (!normalized || products.length >= MAX_COMPARE_PRODUCTS) return;
    const key = getCompareProductKey(normalized);
    if (!key || products.some(p => isSameCompareProduct(p, normalized))) return;

    // Adding only builds the selection — the tray stays open and "Compare"
    // remains the single action that navigates, so the user can add a third
    // medicine before leaving.
    dispatch(addCompareProduct(normalized));
  };
  const removeProduct = product => {
    if (products.length === 1) setIsExpanded(false);
    const productKey_0 = getCompareProductKey(product);
    cancelFlightByProductRef.current(productKey_0);
    dispatch(removeCompareProduct(productKey_0));
  };
  const clearProducts = event_3 => {
    event_3?.preventDefault();
    event_3?.stopPropagation();

    // Clear the authoritative state first. Decorative flight cleanup must not
    // be able to interrupt the action and leave the launcher populated.
    latestProductsLengthRef.current = 0;
    dispatch(clearCompareProducts());
    setIsExpanded(false);

    // Persist the explicit clear synchronously so a fast remount/navigation
    // cannot restore the previous account-scoped selection before the normal
    // persistence effect writes the new Redux state.
    if (storageKey) {
      try {
        window.sessionStorage.removeItem(storageKey);
      } catch {
        // Redux remains the source of truth when browser storage is blocked.
      }
    }
    try {
      cancelAllFlightsRef.current();
    } catch {
      // A decorative animation must never block clearing the selection.
    } finally {
      pendingFlightsRef.current = 0;
      setVisualCount(0);
    }
  };
  const findMedicine = () => {
    setIsExpanded(false);
    router.push("/search");
  };
  return <div ref={trayRef} className="pointer-events-none fixed bottom-20 right-5 z-[70] flex flex-col items-end sm:bottom-8 sm:right-8 lg:bottom-8 lg:right-8">
      <button ref={launcherRef} type="button" aria-expanded={isExpanded} aria-controls="quick-compare-panel" aria-label={`${isExpanded ? "Close" : "Open"} Quick Compare, ${products.length} ${products.length === 1 ? "medicine" : "medicines"} selected`} onClick={() => setIsExpanded(expanded => !expanded)} className="compare-launcher pointer-events-auto inline-flex min-h-12 items-center gap-2.5 rounded-full border border-[#263DD8] bg-gradient-to-r from-[#263EDC] via-[#354EF0] to-[#5268FF] py-2 pl-4 pr-3 font-poppins text-sm font-semibold text-white shadow-[0_10px_28px_rgba(53,78,240,0.34)] transition hover:-translate-y-0.5 hover:from-[#2036C9] hover:via-[#2D46DE] hover:to-[#4359EA] hover:shadow-[0_14px_34px_rgba(53,78,240,0.42)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#354EF0] focus-visible:ring-offset-2 motion-reduce:transform-none">
        <span className="text-base font-black leading-none tracking-[-0.04em]" aria-hidden="true">
          VS
        </span>
        <span className="h-5 w-px bg-white/25" aria-hidden="true" />
        <span key={countPulseToken} className={`inline-flex whitespace-nowrap text-xs font-semibold text-white ${countPulseToken > 0 ? "compare-count-pop" : ""}`} aria-hidden="true">
          {isFirstSelectionLanding ? "Adding…" : `${launcherCount} selected`}
        </span>
        {isExpanded ? <ChevronDown className="h-4 w-4 text-white/80" aria-hidden="true" /> : <ChevronUp className="h-4 w-4 text-white/80" aria-hidden="true" />}
        <span className="sr-only" aria-live="polite">
          {products.length} {products.length === 1 ? "medicine" : "medicines"} selected
        </span>
      </button>

      <section id="quick-compare-panel" aria-labelledby="quick-compare-title" aria-describedby="quick-compare-summary" aria-hidden={!isExpanded} className={`pointer-events-auto absolute bottom-[calc(100%+12px)] right-0 flex max-h-[min(62vh,480px)] w-[calc(100vw-32px)] max-w-[380px] origin-bottom-right flex-col overflow-hidden rounded-2xl border border-[#DCE2FF] bg-white shadow-[0_18px_50px_rgba(31,31,31,0.2)] transition-all duration-200 ease-out motion-reduce:transition-none sm:max-h-[min(70vh,520px)] sm:w-[calc(100vw-80px)] ${isExpanded ? "visible translate-y-0 scale-100 opacity-100" : "invisible translate-y-2 scale-95 opacity-0"}`}>
        <div className="flex flex-shrink-0 items-start justify-between gap-3 border-b border-[#EEEEEE] px-4 py-3.5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#EEF1FF] text-[#354EF0]">
                <GitCompare className="h-4 w-4" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <h2 id="quick-compare-title" className="font-poppins text-sm font-semibold text-[#1F1F1F]">
                  Quick Compare
                </h2>
                <p id="quick-compare-summary" className="font-poppins text-[11px] text-[#737373]">
                  {selectionMessage}
                </p>
              </div>
            </div>
          </div>
          <button type="button" onClick={() => {
          setIsExpanded(false);
          launcherRef.current?.focus();
        }} aria-label="Close Quick Compare" className="flex h-8 w-8 items-center justify-center rounded-full text-[#737373] hover:bg-[#F5F5F5] hover:text-[#1F1F1F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#354EF0]">
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-shrink-0 justify-end px-3 pt-2.5">
          <button type="button" onClick={clearProducts} className="font-poppins text-[11px] font-medium text-[#C83C3C] hover:text-[#A92F2F] hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D95C5C] focus-visible:ring-offset-2">
            Clear all
          </button>
        </div>

        <ol className="space-y-2 px-3 pb-3 pt-2" aria-label={`Selected medicines, ${products.length} of ${MAX_COMPARE_PRODUCTS}`}>
          {products.map((product_0, index) => <li key={getCompareProductKey(product_0)} className="flex min-w-0 items-center gap-3 rounded-xl border border-[#EEEEEE] bg-[#FAFAFA] p-2.5">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#EEF1FF] font-poppins text-xs font-semibold text-[#354EF0]" aria-hidden="true">
                {index + 1}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-poppins text-sm font-semibold text-[#363636]" title={product_0.brand}>
                  {product_0.brand}
                </span>
                <span className="mt-0.5 block truncate font-poppins text-[11px] text-[#8E8E8E]">
                  {typeLabel(product_0.type)}
                  {product_0.generic && product_0.generic.toLowerCase() !== product_0.brand.toLowerCase() ? ` · ${product_0.generic}` : ""}
                </span>
              </span>
              <button type="button" onClick={() => removeProduct(product_0)} aria-label={`Remove ${product_0.brand} from comparison`} className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[#8E8E8E] hover:bg-[#EEEEEE] hover:text-[#1F1F1F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#354EF0]">
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </li>)}
        </ol>

        <div className="flex flex-shrink-0 flex-col gap-2 border-t border-[#EEEEEE] bg-white p-3">
          <div className="flex items-center gap-2">
            {products.length < MAX_COMPARE_PRODUCTS && <button type="button" onClick={findMedicine} className="inline-flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded-lg border border-[#C8D1FF] bg-white px-3 py-2 font-poppins text-xs font-medium text-[#354EF0] hover:bg-[#F8F9FF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#354EF0] focus-visible:ring-offset-1">
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                Add medicine
              </button>}
            <button type="button" onClick={openComparison} disabled={!canCompare} className="inline-flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#354EF0] px-3 py-2 font-poppins text-xs font-semibold text-white transition-colors hover:bg-[#293FD2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#354EF0] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:bg-[#E4E4E4] disabled:text-[#8E8E8E]">
              Compare
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          {!canCompare && <p className="text-center font-poppins text-[11px] text-[#8E8E8E]">
              Add one more medicine to compare.
            </p>}
          {products.length > 0 && products.length < MAX_COMPARE_PRODUCTS && <AddByIndication compact anchor={products[0]} selectedKeys={new Set(products.map(p_0 => getCompareProductKey(p_0)))} selectedProducts={products} onAdd={addSuggested} remainingSlots={MAX_COMPARE_PRODUCTS - products.length} />}
        </div>
        </div>
      </section>

      <style jsx>{`
        @keyframes compare-launcher-in {
          0% {
            opacity: 0;
            transform: translateY(10px) scale(0.92);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes compare-count-roll {
          0% {
            filter: blur(2px);
            opacity: 0;
            transform: translateY(8px) scale(0.84);
          }
          55% {
            filter: blur(0);
            opacity: 1;
            transform: translateY(-2px) scale(1.08);
          }
          100% {
            filter: blur(0);
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .compare-launcher {
          animation: compare-launcher-in 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
        }

        .compare-count-pop {
          animation: compare-count-roll 340ms
            cubic-bezier(0.2, 0.8, 0.2, 1);
        }

        @media (prefers-reduced-motion: reduce) {
          .compare-launcher,
          .compare-count-pop {
            animation: none;
          }
        }
      `}</style>
    </div>;
};
export default CompareTray;